import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Shield, Search, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [actionFilter, setActionFilter] = useState('all');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  
  const limit = 50;
  const skip = (page - 1) * limit;
  const totalPages = Math.ceil(total / limit);

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter, resourceTypeFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString(),
      });

      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (resourceTypeFilter !== 'all') params.append('resource_type', resourceTypeFilter);
      if (searchQuery) params.append('resource_id', searchQuery);

      const res = await axios.get(`${backendUrl}/api/admin/audit?${params}`);
      
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (error) {
      console.error('Erreur chargement audit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const resetFilters = () => {
    setActionFilter('all');
    setResourceTypeFilter('all');
    setSearchQuery('');
    setPage(1);
  };

  const getActionBadge = (action) => {
    const configs = {
      user_created: { variant: 'default', text: 'Création utilisateur', icon: '➕' },
      user_updated: { variant: 'secondary', text: 'Modification utilisateur', icon: '✏️' },
      user_suspended: { variant: 'destructive', text: 'Suspension', icon: '🚫' },
      user_unsuspended: { variant: 'default', text: 'Réactivation', icon: '✅' },
      user_deleted: { variant: 'destructive', text: 'Suppression', icon: '🗑️' },
      role_changed: { variant: 'default', text: 'Changement de rôle', icon: '🔄' },
      password_reset: { variant: 'secondary', text: 'Reset password', icon: '🔑' },
      email_verified: { variant: 'secondary', text: 'Email vérifié', icon: '✉️' },
      settings_updated: { variant: 'secondary', text: 'Paramètres modifiés', icon: '⚙️' },
    };
    
    const config = configs[action] || { variant: 'outline', text: action, icon: '📋' };
    return (
      <div className="flex items-center gap-2">
        <span>{config.icon}</span>
        <Badge variant={config.variant}>{config.text}</Badge>
      </div>
    );
  };

  const getResourceBadge = (resourceType) => {
    const configs = {
      user: { variant: 'default', text: 'Utilisateur', icon: '👤' },
      contract: { variant: 'secondary', text: 'Contrat', icon: '📄' },
      payment: { variant: 'secondary', text: 'Paiement', icon: '💳' },
      settings: { variant: 'outline', text: 'Paramètres', icon: '⚙️' },
    };
    
    const config = configs[resourceType] || { variant: 'outline', text: resourceType, icon: '📦' };
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs">{config.icon}</span>
        <Badge variant={config.variant} className="text-xs">{config.text}</Badge>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-6" data-testid="audit-log-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Shield className="h-8 w-8" />
          Journal d'Audit
        </h1>
        <p className="text-muted-foreground mt-2">
          {total} action{total > 1 ? 's' : ''} enregistrée{total > 1 ? 's' : ''}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Action Filter */}
              <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
                <SelectTrigger data-testid="action-filter">
                  <SelectValue placeholder="Toutes les actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les actions</SelectItem>
                  <SelectItem value="user_created">Création utilisateur</SelectItem>
                  <SelectItem value="user_updated">Modification utilisateur</SelectItem>
                  <SelectItem value="user_suspended">Suspension</SelectItem>
                  <SelectItem value="user_unsuspended">Réactivation</SelectItem>
                  <SelectItem value="role_changed">Changement rôle</SelectItem>
                  <SelectItem value="email_verified">Email vérifié</SelectItem>
                  <SelectItem value="password_reset">Reset password</SelectItem>
                </SelectContent>
              </Select>

              {/* Resource Type Filter */}
              <Select value={resourceTypeFilter} onValueChange={(v) => { setResourceTypeFilter(v); setPage(1); }}>
                <SelectTrigger data-testid="resource-filter">
                  <SelectValue placeholder="Toutes les ressources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les ressources</SelectItem>
                  <SelectItem value="user">Utilisateur</SelectItem>
                  <SelectItem value="contract">Contrat</SelectItem>
                  <SelectItem value="payment">Paiement</SelectItem>
                  <SelectItem value="settings">Paramètres</SelectItem>
                </SelectContent>
              </Select>

              {/* Search by Resource ID */}
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par ID ressource..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" size="sm">
                Rechercher
              </Button>
              {(actionFilter !== 'all' || resourceTypeFilter !== 'all' || searchQuery) && (
                <Button type="button" variant="ghost" size="sm" onClick={resetFilters}>
                  Réinitialiser
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Audit Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Historique des Actions Admin
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucune entrée d'audit trouvée</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {logs.map((log, idx) => (
                  <div 
                    key={idx} 
                    className="border-l-4 border-primary/40 pl-4 py-3 hover:bg-muted/30 transition-colors rounded-r-lg"
                    data-testid={`audit-log-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-2">
                        {/* Action Title */}
                        <h3 className="text-base font-semibold">
                          {log.action ? log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Action non spécifiée'}
                        </h3>
                        
                        {/* Action & Resource Type Badges */}
                        <div className="flex items-center gap-3">
                          {getActionBadge(log.action)}
                          {getResourceBadge(log.resource_type)}
                        </div>

                        {/* Admin Info */}
                        <div className="text-sm">
                          <span className="font-semibold">Par :</span>{' '}
                          <span className="text-muted-foreground">{log.admin_email || 'Non spécifié'}</span>
                          {log.admin_user_id && (
                            <span className="text-xs text-muted-foreground ml-2">
                              (ID: {log.admin_user_id.substring(0, 8)})
                            </span>
                          )}
                        </div>

                        {/* Resource Info */}
                        {log.resource_id && (
                          <div className="text-sm">
                            <span className="font-semibold">Ressource :</span>{' '}
                            <code className="px-2 py-0.5 bg-muted rounded text-xs">
                              {log.resource_id.substring(0, 16)}...
                            </code>
                          </div>
                        )}

                        {/* Details */}
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="text-sm bg-muted/50 p-2 rounded">
                            <span className="font-semibold">Détails :</span>
                            <pre className="text-xs mt-1 whitespace-pre-wrap">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </div>
                        )}

                        {/* Reason */}
                        {log.reason && (
                          <div className="text-sm">
                            <span className="font-semibold">Raison :</span>{' '}
                            <span className="text-muted-foreground">{log.reason}</span>
                          </div>
                        )}

                        {/* Timestamp */}
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(log.timestamp), 'dd/MM/yyyy à HH:mm:ss', { locale: fr })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-6 border-t">
                  <div className="text-sm text-muted-foreground">
                    Page {page} sur {totalPages} • {total} entrée{total > 1 ? 's' : ''}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                      data-testid="prev-page-button"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => setPage(page + 1)}
                      data-testid="next-page-button"
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
